import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { CheckCircle, ArrowRight, Phone, Accessibility, Home, Clock, Euro, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import ContactModal from "../components/ContactModal";
import Seo from "../components/Seo";
import { serviceJsonLd, filArianeJsonLd } from "../utils/donneesStructurees";
import BoutonRappel from "../components/BoutonRappel";
import GuidesLies from "../components/GuidesLies";

const AccessibilitePage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  
  const benefits = [
    "Maintien de l'autonomie à domicile",
    "Prévention des chutes et accidents", 
    "Circulation facilitée dans le logement",
    "Confort d'utilisation au quotidien",
    "Adaptation évolutive selon les besoins",
    "Valorisation du patrimoine immobilier"
  ];

  const solutions = [
    {
      title: "Monte-escalier",
      description: "Fauteuil sur escalier droit, rail sur mesure pour escalier tournant, ou plateforme élévatrice",
      price: "3 500 - 25 000€",
      duration: "1-2 jours"
    },
    {
      title: "Rampes d'accès", 
      description: "Rampes fixes ou amovibles pour franchir les seuils et marches",
      price: "2 500 - 8 000€",
      duration: "1 jour"
    },
    {
      title: "Aménagement global",
      description: "Élargissement portes, barres d'appui, automatisation",
      price: "12 000 - 22 000€",
      duration: "3-7 jours"
    }
  ];

  const equipments = [
    "Monte-escaliers droits et courbes",
    "Plateformes élévatrices verticales", 
    "Rampes d'accès intérieures et extérieures",
    "Élargissement des ouvertures",
    "Barres d'appui et main courantes",
    "Automatisation des portes",
    "Éclairage adapté des circulations",
    "Revêtements antidérapants"
  ];

  const process = [
    {
      step: "1",
      title: "Évaluation des besoins",
      description: "Diagnostic autonomie liberté par notre visiteur spécialiste pour identifier les aménagements nécessaires"
    },
    {
      step: "2", 
      title: "Solutions personnalisées",
      description: "Proposition d'équipements adaptés avec plan technique et estimation financière"
    },
    {
      step: "3",
      title: "Dossier MaPrimeAdapt'",
      description: "Constitution complète du dossier d'aide avec suivi administratif"
    },
    {
      step: "4",
      title: "Installation professionnelle",
      description: "Pose par nos techniciens certifiés avec formation à l'utilisation"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Seo
        titre="Accessibilité du logement senior et PMR | MaPrimeAdapt'"
        description="Monte-escalier, élargissement de portes, main courante, éclairage adapté : nos solutions d'accessibilité financées jusqu'à 70 % par MaPrimeAdapt'."
        chemin="/accessibilite"
        donnees={[
          serviceJsonLd({
            nom: "Solutions d'accessibilité du logement",
            description: "Monte-escalier, élargissement de portes, mains courantes et éclairage adapté pour sécuriser le logement.",
            url: "/accessibilite",
          }),
          filArianeJsonLd([
            { nom: "Accueil", url: "/" },
            { nom: "Accessibilité du logement", url: "/accessibilite" },
          ]),
        ]}
      />
      <Header />

      <main id="contenu">
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-white via-gray-50 to-white py-20 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'url("/images/fond-accessibilite.webp")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 to-gray-50/95"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center px-4 py-2 bg-green-700 text-white rounded-full text-sm font-medium">
                  <Accessibility className="h-4 w-4 mr-2" />
                  Solutions d'accessibilité • MaPrimeAdapt'
                </div>
                
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                  Accessibilité PMR Domicile : Monte-escaliers{" "}
                  <span className="text-green-700 block mt-2">et Aménagements MaPrimeAdapt' 2026</span>
                </h1>
                
                <p className="text-xl text-gray-900 leading-relaxed">
                  <strong>Expert adaptation logement PMR France</strong> : monte-escaliers, rampes d'accès, élargissement portes aux normes accessibilité. 
                  AMO habilité ANAH, dossier MaPrimeAdapt' complet avec artisans certifiés RGE. 
                  <strong className="text-green-700">Jusqu'à 15 400€ d'aides financières</strong> selon revenus, pour des travaux plafonnés à 22 000€.
                </p>
                <p className="text-lg text-gray-700">
                  Votre projet porte sur un escalier ?{" "}
                  <Link to="/monte-escalier" className="text-orange-700 underline hover:text-orange-800">
                    Voir le prix d'un monte-escalier et le reste à charge après aides
                  </Link>
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/simulateur">
                  <Button size="lg" className="bg-green-700 hover:bg-green-800 text-white shadow-lg group w-full sm:w-auto">
                    Tester mon éligibilité
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-green-200 hover:bg-green-50 w-full sm:w-auto"
                  onClick={() => setIsContactModalOpen(true)}
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Nous contacter
                </Button>
                
                <BoutonRappel className="border-2 border-green-200 hover:bg-green-50 w-full sm:w-auto" />
              </div>

              <div className="grid grid-cols-3 gap-6 pt-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-700">1-7 jours</div>
                  <div className="text-sm text-gray-700">Installation</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-700">22 000€</div>
                  <div className="text-sm text-gray-700">Plafond de travaux</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-700">289</div>
                  <div className="text-sm text-gray-700">Dossiers réalisés</div>
                </div>
              </div>
            </div>

            {/* Example Box avec image */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
              <img 
                src="/images/monte-escalier.webp"
                width="700"
                height="700"
                loading="eager" 
                alt="Accessibilité PMR - Monte-escalier et aménagements" 
                className="w-full h-64 object-cover"
              />
              <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">
                Exemple M. Martin : Monte-escalier
              </h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-700">Coût de l'équipement</span>
                  <span className="font-semibold text-gray-900">8 000 €</span>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-green-700">MaPrimeAdapt' (70%)</span>
                    <span className="font-semibold text-green-700">- 5 600 €</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-green-700">Aide complémentaire</span>
                    <span className="font-semibold text-green-700">0 €</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-green-700">AMO (pris en charge)</span>
                    <span className="font-semibold text-green-700">0 €</span>
                  </div>
                </div>

                <div className="pt-4 border-t-2 border-green-200">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">Reste à charge</span>
                    <span className="text-2xl font-bold text-green-700">2 400 €</span>
                  </div>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg mt-4">
                  <p className="text-sm text-green-800">
                    <strong>Économie réalisée :</strong> 5 600€ grâce à MaPrimeAdapt'
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* Section des adaptations principales */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Adaptations Accessibilité PMR : Normes et Équipements Obligatoires 2026
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              <strong>Conformité réglementation française</strong> : monte-escaliers, rampes PMR, élargissement portes selon normes ANAH. 
              Accompagnement AMO expert pour dossier MaPrimeAdapt' avec artisans certifiés Handibat et Silverbat partout en France.
            </p>
          </div>

          {/* Section explicative accessibilité PMR */}
          <div className="mb-16">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-12">
              <h3 className="text-xl font-semibold text-green-800 mb-4">
                Accessibilité PMR Logement : Réglementation et Normes Françaises 2026
              </h3>
              <p className="text-green-700 leading-relaxed mb-4">
                L'<strong>accessibilité PMR</strong> respecte la loi française du handicap et les normes ANAH pour seniors et personnes handicapées. 
                Espaces de circulation 150 cm, largeurs portes 90 cm, hauteurs équipements adaptées, monte-escaliers certifiés NF, 
                rampes conformes pente réglementaire 5% maximum selon DTU 36.1.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mt-6">
                <div>
                  <h4 className="font-semibold text-green-800 mb-3">🔧 Normes techniques obligatoires :</h4>
                  <ul className="text-sm text-green-700 space-y-2">
                    <li>• <strong>Monte-escalier :</strong> Certification CE obligatoire</li>
                    <li>• <strong>Rampes PMR :</strong> Pente max 5%, largeur 90 cm</li>
                    <li>• <strong>Portes :</strong> Largeur libre 80 cm minimum</li>
                    <li>• <strong>Paliers :</strong> 150 x 150 cm minimum</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-green-800 mb-3">✅ Aides financières disponibles :</h4>
                  <ul className="text-sm text-green-700 space-y-2">
                    <li>• MaPrimeAdapt' jusqu'à 15 400€ d'aides</li>
                    <li>• MDPH PCH aide technique</li>
                    <li>• Caisses retraite CARSAT, MSA</li>
                    <li>• Collectivités territoriales</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Grid 2x2 des adaptations accessibilité */}
            <div className="grid md:grid-cols-2 gap-8">
              {/* Monte-escalier */}
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                <div className="flex items-start space-x-6">
                  <div className="w-32 h-32 bg-white rounded-lg flex items-center justify-center flex-shrink-0 p-3 shadow-md">
                    <img 
                      src="/images/wc-sureleve.webp"
                      width="256"
                      height="197"
                      loading="lazy" 
                      alt="Monte-escalier PMR" 
                      className="w-full h-full object-cover rounded"
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-blue-700 mb-3">
                      Monte-escalier certifié PMR
                    </h4>
                    <p className="text-sm text-gray-700 leading-relaxed mb-3">
                      Installation de fauteuils élévateurs et plateformes pour escaliers droits, courbes ou extérieurs. 
                      Conformité CE obligatoire, télécommandes, sièges pivotants, détecteurs d'obstacles. 
                      Charge utile 120-160 kg selon modèles.
                    </p>
                    <div className="bg-blue-100 p-3 rounded text-xs text-blue-800">
                      <strong>Prix :</strong> 4 000-15 000€ • <strong>Installation :</strong> 1-2 jours • 
                      <strong>MaPrimeAdapt' :</strong> jusqu'à 70% pris en charge
                    </div>
                  </div>
                </div>
              </div>

              {/* Rampe d'accès PMR */}
              <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                <div className="flex items-start space-x-6">
                  <div className="w-32 h-32 bg-white rounded-lg flex items-center justify-center flex-shrink-0 p-3 shadow-md">
                    <img 
                      src="/images/rampe-inox.webp"
                      width="400"
                      height="400"
                      loading="lazy" 
                      alt="Rampe d'accès PMR" 
                      className="w-full h-full object-cover rounded"
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-green-700 mb-3">
                      Rampes d'accès conformes PMR
                    </h4>
                    <p className="text-sm text-gray-700 leading-relaxed mb-3">
                      Rampes fixes aluminium, bois ou béton pour franchir seuils et marches. 
                      Pente réglementaire 5% maximum, largeur 90 cm minimum, garde-corps 
                      obligatoires si hauteur > 40 cm. Revêtement antidérapant certifié R11.
                    </p>
                    <div className="bg-green-100 p-3 rounded text-xs text-green-800">
                      <strong>Prix :</strong> 800-5 000€ • <strong>Pente :</strong> 5% max DTU • 
                      <strong>Largeur :</strong> 90 cm réglementaire
                    </div>
                  </div>
                </div>
              </div>

              {/* Élargissement portes */}
              <div className="bg-orange-50 p-6 rounded-lg border border-orange-200">
                <div className="flex items-start space-x-6">
                  <div className="w-32 h-32 bg-white rounded-lg flex items-center justify-center flex-shrink-0 p-3 shadow-md">
                    <img 
                      src="/images/elargissement-portes.webp"
                      width="728"
                      height="563"
                      loading="lazy" 
                      alt="Élargissement portes PMR" 
                      className="w-full h-full object-cover rounded"
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-orange-700 mb-3">
                      Élargissement portes et ouvertures
                    </h4>
                    <p className="text-sm text-gray-700 leading-relaxed mb-3">
                      Adaptation largeur passages pour fauteuils roulants : portes 80 cm minimum, 
                      suppression seuils, automatisation motorisée. Poignées ergonomiques 
                      hauteur 90-130 cm, effort ouverture réduit &lt; 50 N selon norme NFP 01-012.
                    </p>
                    <div className="bg-orange-100 p-3 rounded text-xs text-orange-800">
                      <strong>Prix :</strong> 1 500-4 000€ • <strong>Largeur :</strong> 80 cm libre • 
                      <strong>Seuils :</strong> 2 cm max autorisé
                    </div>
                  </div>
                </div>
              </div>

              {/* Barres d'appui et automatismes */}
              <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
                <div className="flex items-start space-x-6">
                  <div className="w-32 h-32 bg-white rounded-lg flex items-center justify-center flex-shrink-0 p-3 shadow-md">
                    <img 
                      src="/images/main-courante.webp"
                      width="640"
                      height="640"
                      loading="lazy" 
                      alt="Barres d'appui PMR" 
                      className="w-full h-full object-cover rounded"
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-purple-700 mb-3">
                      Barres d'appui et automatismes PMR
                    </h4>
                    <p className="text-sm text-gray-700 leading-relaxed mb-3">
                      Installation barres de maintien certifiées NF, main-courantes continues, 
                      automatisation portes et volets. Éclairage adapté, interrupteurs 
                      à hauteur accessible, domotique vocale pour autonomie renforcée.
                    </p>
                    <div className="bg-purple-100 p-3 rounded text-xs text-purple-800">
                      <strong>Prix :</strong> 500-3 000€ • <strong>Charge :</strong> 100 kg min • 
                      <strong>Hauteur :</strong> 70-80 cm réglementaire
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Prix Équipements Accessibilité PMR avec MaPrimeAdapt' 2026
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              <strong>Tarifs adaptations logement PMR France</strong> : monte-escaliers certifiés, rampes conformes DTU, 
              élargissement portes normes accessibilité. Installation artisans RGE avec garanties constructeur, 
              dossiers MaPrimeAdapt' jusqu'à 15 400€ d'aides selon plafonds revenus ANAH.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {solutions.map((solution, index) => (
              <Card key={index} className="border-2 hover:border-green-200 transition-all duration-300 hover:shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl text-gray-900 mb-2">
                    {solution.title}
                  </CardTitle>
                  <CardDescription className="text-gray-700">
                    {solution.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      <Euro className="h-3 w-3 mr-1" />
                      {solution.price}
                    </Badge>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      <Clock className="h-3 w-3 mr-1" />
                      {solution.duration}
                    </Badge>
                  </div>
                  
                  <Link to="/simulateur">
                    <Button className="w-full bg-green-700 hover:bg-green-800">
                      Obtenir un devis
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Equipment List */}
          <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Équipements d'accessibilité disponibles
            </h3>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {equipments.map((equipment, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700 text-sm">{equipment}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Pourquoi améliorer l'accessibilité ?
              </h2>
              
              <div className="space-y-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-green-700" />
                    </div>
                    <span className="text-gray-700 text-lg">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-green-50 rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">
                MaPrimeAdapt' : jusqu'à 15 400€ d'aides
              </h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Home className="h-6 w-6 text-green-700" />
                  <span className="text-gray-700">Propriétaires occupants éligibles</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Accessibility className="h-6 w-6 text-green-700" />
                  <span className="text-gray-700">Locataires du parc privé acceptés</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-700" />
                  <span className="text-gray-700">Accompagnement AMO gratuit inclus</span>
                </div>
              </div>
              
              <Link to="/simulateur" className="mt-6 block">
                <Button className="w-full bg-green-700 hover:bg-green-800">
                  Vérifier mon éligibilité
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Notre méthode d'accompagnement
            </h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Un processus structuré pour garantir la réussite de votre projet d'accessibilité
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-green-700 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {step.step}
                  </div>
                  {index < 3 && (
                    <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-green-200 transform -translate-y-1/2"></div>
                  )}
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h4>
                <p className="text-gray-700 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-green-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-2xl lg:text-3xl font-bold mb-4">
            Améliorez votre accessibilité dès aujourd'hui
          </h3>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Nos experts vous accompagnent pour trouver les solutions les plus adaptées à vos besoins
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-lg mx-auto">
            <Link to="/simulateur" className="w-full sm:w-auto">
              <Button 
                size="lg" 
                className="bg-white text-green-700 hover:bg-gray-100 w-full sm:w-auto shadow-lg"
              >
                Simulation gratuite
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white hover:text-blue-600 w-full sm:w-auto"
              onClick={() => setIsContactModalOpen(true)}
            >
              <MessageCircle className="mr-2 h-5 w-5" />
              Nous contacter
            </Button>
            
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white hover:text-green-700 w-full sm:w-auto"
              onClick={() => window.location.href = 'tel:0939035444'}
            >
              <Phone className="mr-2 h-5 w-5" />
              09 39 03 54 44
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section optimisée SEO */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Questions Fréquentes : Accessibilité PMR et MaPrimeAdapt' 2026
            </h2>
            
            <div className="space-y-8">
              <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Quel est le prix d'un monte-escalier avec MaPrimeAdapt' en 2026 ?
                </h3>
                <p className="text-gray-700">
                  Prix monte-escalier PMR : <strong>4 000€ à 15 000€</strong> selon modèle (droit/courbe). 
                  Avec MaPrimeAdapt', prise en charge jusqu'à <strong>70% du coût</strong> (soit 15 400€ d'aide maximum, pour des travaux plafonnés à 22 000€). 
                  Installation certifiée CE par techniciens agréés, garantie constructeur 2-5 ans incluse.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Quelles normes PMR respecter pour une rampe d'accès domicile ?
                </h3>
                <p className="text-gray-700">
                  <strong>Normes rampe PMR France</strong> : pente maximum 5% (DTU 36.1), largeur 90 cm minimum, 
                  paliers repos tous les 10 mètres, garde-corps obligatoires si hauteur > 40 cm. 
                  Revêtement antidérapant R11, résistance charge 400 kg/m². Conformité réglementation accessibilité 2026.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Comment obtenir MaPrimeAdapt' pour adaptations accessibilité logement ?
                </h3>
                <p className="text-gray-700">
                  <strong>Procédure MaPrimeAdapt' accessibilité</strong> : diagnostic gratuit AMO habilité ANAH, 
                  évaluation besoins ergothérapeute, devis artisans certifiés RGE/Handibat, dossier ANAH complet. 
                  Éligibilité : 70 ans et plus ou GIR 1-6, revenus modestes selon plafonds, résidence principale uniquement.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Quels délais pour installer équipements PMR avec MaPrimeAdapt' ?
                </h3>
                <p className="text-gray-700">
                  <strong>Délais installation accessibilité PMR</strong> : dossier MaPrimeAdapt' traité 2-4 mois ANAH, 
                  monte-escalier installé 1-2 jours, rampes PMR 1 jour, élargissement portes 2-3 jours. 
                  Formation utilisation équipements incluse, maintenance préventive recommandée annuelle.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <GuidesLies slugs={["travaux-accessibilite-copropriete", "gir-evaluation-autonomie"]} />

      </main>

      <Footer />
      
      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        type="general"
      />
    </div>
  );
};

export default AccessibilitePage;